import { hasPermission } from "../context/AuthContext";

/** Human label for a contract allocation line on this truck. */
export function contractAllocLabel(alloc) {
  if (!alloc) return "—";
  const num = alloc.contract_number || alloc.purchase_contract_id;
  const variety = alloc.variety || "—";
  const rate = alloc.rate_per_qtl != null ? `₹${alloc.rate_per_qtl}/qtl` : "";
  return [num, variety, rate].filter(Boolean).join(" · ");
}

/** Distinct paddy qualities on this truck (from contract allocations or purchase variety). */
export function purchaseDistinctQualities(purchase) {
  const allocs = purchase?.contract_allocations || [];
  if (allocs.length === 0) {
    const v = String(purchase?.variety || "").trim();
    return v ? [v] : [];
  }
  const seen = new Set();
  const out = [];
  for (const a of allocs) {
    const v = String(a.variety || "").trim();
    const key = v.toUpperCase();
    if (v && !seen.has(key)) {
      seen.add(key);
      out.push(v);
    }
  }
  return out;
}

/** Unloader picks a contract only when the truck carries more than one quality. */
export function purchaseRequiresUnloaderContractPick(purchase) {
  return purchaseDistinctQualities(purchase).length > 1;
}

/** Summary of all contracts on a purchase (variety + rate each). */
export function purchaseContractsSummary(purchase) {
  const allocs = purchase?.contract_allocations || [];
  if (allocs.length === 0) return purchase?.variety || "—";
  if (allocs.length === 1) return contractAllocLabel(allocs[0]);
  return allocs.map(contractAllocLabel).join(" | ");
}

/** Purchase states where unloaders may edit destination lines (before tare / accounts). */
export const UNLOADING_EDITABLE_STATES = ["APPROVED", "UNLOADING", "UNLOADING_HOLD"];

/** Admin may correct unloading even after weighbridge mid/tare (not after COMPLETED). */
export const ADMIN_UNLOADING_EDITABLE_STATES = [
  ...UNLOADING_EDITABLE_STATES,
  "WEIGH_MID",
  "WEIGH_TARE",
  "ACCOUNTS_PENDING",
  "COMPLETED",
];

/** Purchase had accounts finalization (voucher frozen / truck completed). */
export function purchaseHasFinalizedAccounts(purchase) {
  if (!purchase) return false;
  if (purchase.state === "COMPLETED") return true;
  if (purchase.accounts_refinalize_required) return true;
  if (purchase.accounts_finalized === true) return true;
  const vouchers = purchase.vouchers || [];
  if (vouchers.length === 0) {
    const v = purchase.voucher;
    return Boolean(v?.settlement_frozen || v?.deductions?.settlement_frozen);
  }
  return vouchers.some((v) => v.settlement_frozen || v.deductions?.settlement_frozen);
}

/** Match backend weigh-correction rules: allow post-finalize corrections (reopens voucher). */
export function canWeighCorrectPurchase(purchase) {
  if (!purchase || purchase.state === "REJECTED") return false;
  const hasVoucher = (purchase.vouchers?.length ?? 0) > 0 || Boolean(purchase.voucher);
  if (!hasVoucher) return true;
  return purchaseHasFinalizedAccounts(purchase);
}

/** True when weighbridge has applied qtl to this destination line. */
export function isUnloadDestinationFinalized(dest) {
  return Boolean(dest?.weight_applied) || Number(dest?.weight_qtl) > 0;
}

export function canEditUnloadDestinations(purchase, user = null) {
  const state = purchase?.state;
  if (!state || state === "REJECTED") return false;
  if (user && hasPermission(user, "unloading.create")) return true;
  if (user?.role === "admin") {
    return ADMIN_UNLOADING_EDITABLE_STATES.includes(state);
  }
  return UNLOADING_EDITABLE_STATES.includes(state);
}

/** Edit lines that already have weighbridge weight (admin, or unloader after voucher finalize). */
export function canEditFinalizedUnloadLines(user, purchase) {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (purchaseHasFinalizedAccounts(purchase) && hasPermission(user, "unloading.create")) {
    return true;
  }
  return false;
}

/** @deprecated use canEditFinalizedUnloadLines(user, purchase) */
export function canAdminEditFinalizedUnloadLines(user, purchase = null) {
  return canEditFinalizedUnloadLines(user, purchase);
}

/** Default destination row for unloading form. */
export function defaultUnloadDestination({ loose = false, stacks = [], allocations = [], purchase = null } = {}) {
  const allocs = allocations.length ? allocations : purchase?.contract_allocations || [];
  const purchaseCtx = purchase || { contract_allocations: allocs };
  const requiresContractPick = purchaseRequiresUnloaderContractPick(purchaseCtx);
  const qualities = purchaseDistinctQualities(purchaseCtx);
  const first = allocs[0];
  const variety = qualities[0] || first?.variety || "1509";
  const purchase_contract_id =
    requiresContractPick && first?.purchase_contract_id != null ? String(first.purchase_contract_id) : "";
  const production_batch_id =
    purchase?.production_batch_id != null
      ? String(purchase.production_batch_id)
      : purchase?.unloading_active_batch_id != null
        ? String(purchase.unloading_active_batch_id)
        : "";
  if (loose) {
    return {
      location_type: "HOUDI",
      location_id: "H-1",
      variety,
      purchase_contract_id,
      production_batch_id,
      bags: "",
      stack_height_tier: "BASE",
      carry_distance_tier: "BASE",
    };
  }
  return {
    location_type: "HOUDI",
    location_id: "H-1",
    variety,
    purchase_contract_id,
    production_batch_id,
    bags: "",
    weight_qtl: 0,
    stack_height_tier: "BASE",
    carry_distance_tier: "BASE",
  };
}

/** Purchase still in yard workflow — cannot start a second purchase on same gate pass. */
export const OPEN_PURCHASE_STATES = new Set([
  "GATE_IN",
  "WEIGH_GROSS",
  "LAB_PENDING",
  "BARGAINING",
  "APPROVED",
  "UNLOADING",
  "UNLOADING_HOLD",
  "RELAB_PENDING",
  "WEIGH_MID",
  "WEIGH_TARE",
  "ACCOUNTS_PENDING",
]);

/** States where weighbridge operations (gross / mid / tare) may still be pending. */
export const WEIGHBRIDGE_OPERATION_STATES = new Set([
  "GATE_IN",
  "WEIGH_GROSS",
  "WEIGH_MID",
  "UNLOADING",
]);

export function purchaseBlocksNewStart(purchase) {
  return Boolean(purchase?.state && OPEN_PURCHASE_STATES.has(purchase.state));
}

/** Gate pass already used for a purchase this visit (one pass → one truck workflow). */
export function gatePassHasPurchase(gatePassId, purchases) {
  return (purchases || []).some(
    (p) => String(p.gate_pass_id) === String(gatePassId) && p.state !== "REJECTED",
  );
}

/** IN-yard passes with no purchase started on this pass yet. */
export function gatePassesAvailableForStart(gatePasses, purchases) {
  return (gatePasses || []).filter((gp) => !gatePassHasPurchase(gp.id, purchases));
}

export function purchaseNeedsGrossWeigh(purchase) {
  return purchase?.state === "GATE_IN";
}

export function purchaseNeedsRouteAfterGross(purchase) {
  return purchase?.state === "WEIGH_GROSS";
}

export function purchaseNeedsMidWeigh(purchase) {
  return purchase?.state === "WEIGH_MID";
}

/** Awaiting final or segment tare — only after gross and unloading has started. */
export function purchaseNeedsTareWeigh(purchase) {
  if (!purchase?.gross_weight_qtl || purchase.tare_weight_qtl) return false;
  if (!["UNLOADING"].includes(purchase.state)) return false;
  return true;
}

export function purchaseNeedsWeightRecording(purchase) {
  return purchaseWeighKind(purchase) != null;
}

export function purchaseWeighKind(purchase) {
  if (purchaseNeedsGrossWeigh(purchase)) return "gross";
  if (purchaseNeedsMidWeigh(purchase)) return "mid";
  if (purchaseNeedsTareWeigh(purchase)) return "tare";
  return null;
}

export function purchaseWeighStepLabel(purchase) {
  const kind = purchaseWeighKind(purchase);
  if (kind === "gross") return "Gross weigh-in";
  if (kind === "mid") return "Mid weigh";
  if (kind === "tare") return "Tare / final";
  return "Record weight";
}

export function purchaseInWeighbridgeQueue(purchase) {
  return purchaseNeedsWeightRecording(purchase);
}

/** True when every selected weighbridge contract line is bardana LOOSE. */
export function contractsUnloadByQtl(contractLines, pendingContracts) {
  if (!contractLines?.length) return false;
  return contractLines.every((ln) => {
    const c = pendingContracts.find((x) => String(x.id) === String(ln.purchase_contract_id));
    return c?.bardana === "LOOSE";
  });
}
